<template>
    <div v-if="showControls" ref="root" class="grid-container mt-14 lg:mt-18" :class="extraClass">
        <!--        I control your carousel! It has {{ cols }} cols, {{ visibleCols }} visible. Act. dot: {{ activeDot }} - RMC: {{ rightMostCol }} &#45;&#45;-->
        <div class="flex">
            <div class="mx-auto flex items-center gap-x-4 stroke-current">
                <button
                    class="mr-1 rotate-180 sm:mr-5"
                    :class="
                        color === 'white' || color === 'sand'
                            ? 'text-anthrazit-60 hover:text-anthrazit-90 disabled:text-sand-100'
                            : 'text-white/60 hover:text-white disabled:text-sand-100/30'
                    "
                    :disabled="rightMostCol + 1 <= visibleCols"
                    @click="prevPage"
                    aria-label="arrow left">
                    <svg-icon name="chevron-right" />
                </button>
                <button
                    v-for="(val, idx) in Math.ceil(cols / visibleCols)"
                    :key="idx"
                    :class="
                        color === 'white'
                            ? activeDot === idx
                                ? 'h-3 w-3 bg-anthrazit-90'
                                : 'h-2.5 w-2.5 bg-sand-100 hover:bg-anthrazit-50'
                            : color === 'sand'
                              ? activeDot === idx
                                  ? 'h-3 w-3 bg-anthrazit-90'
                                  : 'h-2.5 w-2.5 bg-white hover:bg-anthrazit-50'
                              : activeDot === idx
                                ? 'h-3 w-3 bg-white'
                                : 'h-2.5 w-2.5 bg-sand-100/30 hover:bg-sand-100/60'
                    "
                    class="block rounded-full hidden md:block"
                    @click="goToPage(idx)"
                    :aria-label="'dot ' + idx"/>
                <button
                    class="ml-1 sm:ml-5"
                    :class="
                        color === 'white' || color === 'sand'
                            ? 'text-anthrazit-60 hover:text-anthrazit-90 disabled:text-sand-100'
                            : 'text-white/60 hover:text-white disabled:text-sand-100/30'
                    "
                    :disabled="rightMostCol + 1 >= cols"
                    @click="nextPage"
                    aria-label="arrow right">
                    <svg-icon name="chevron-right" />
                </button>
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import SvgIcon from "@components/SvgIcon.vue"
import tailwindConfig from "tailwind.config"

const props = withDefaults(
    defineProps<{
        color?: "white" | "sand" | "tanne" | "anthrazit"
        calculate?: boolean
        extraClass?: string
        multiRow?: boolean
    }>(),
    {
        color: "white",
        calculate: true,
        extraClass: "",
        multiRow: false,
    }
)

const root = ref<HTMLDivElement>(null)

let scroller = ref<HTMLElement>(null)
let grid = ref<HTMLElement>(null)
let cols = ref(0) // total cols (= elements)
const visibleCols = ref(3) // depends on breakpoint
let activeDot = ref(0)
let rightMostCol = ref(0)
let showControls = ref(true) // should the controls be visible, depends on current breakpoint and number of cols

onMounted(() => {
    scroller.value = root.value.parentElement?.previousElementSibling?.children[0] as HTMLElement
    if (scroller.value) {
        grid.value = scroller.value.children[0] as HTMLElement
        cols.value = grid.value.children.length
        if (cols.value > 1) {
            scroller.value.addEventListener("scroll", onScroll)
        }
    }
    checkBreakpoint()
    if (cols.value > 1) {
        window.addEventListener("resize", checkBreakpoint, { passive: true })
    }
})

onBeforeUnmount(() => {
    if (cols.value > 1) {
        if (scroller.value) {
            scroller.value.removeEventListener("scroll", onScroll)
        }
        window.removeEventListener("resize", checkBreakpoint)
    }
})

function checkBreakpoint() {
    if (window.innerWidth >= parseInt(tailwindConfig.theme.screens.lg)) {
        visibleCols.value = 3
        showControls.value = props.multiRow ? false : cols.value > 3
    } else if (window.innerWidth < parseInt(tailwindConfig.theme.screens.sm)) {
        visibleCols.value = 1
        showControls.value = cols.value > 1
    } else {
        visibleCols.value = 2
        showControls.value = cols.value > 2
    }
    checkVis()
}

function checkVis() {
    if(props.calculate){
        calculateContainerWidth()
    }
    const parentLeft = scroller.value.getBoundingClientRect().left
    // alternative method (switch to next dot as soon as one element of the next page is visible
    let width = scroller.value.clientWidth
    const childs = grid.value.children
    if (visibleCols.value === 2) {
        width = childs[1].getBoundingClientRect().right - parentLeft - (childs[0].getBoundingClientRect().left - parentLeft)
    } else if (visibleCols.value === 1) {
        width = childs[0].getBoundingClientRect().right - parentLeft - (childs[0].getBoundingClientRect().left - parentLeft)
    }
    // console.debug("scroll area width: " + width)
    const leftWithTolerance = visibleCols.value > 1 ? 0 : -3
    let rmc = -1
    for (let i = 0; i < cols.value; i++) {
        const left = childs[i].getBoundingClientRect().left - parentLeft
        if (left >= leftWithTolerance && left < width) {
            rmc = Math.max(rmc, i)
        }
    }
    if (rmc >= 0) {
        // when in 1-col mode, cols sometimes dont match rmc condition above, so we then dont change rightMostCol
        rightMostCol.value = rmc
    }
    activeDot.value = Math.ceil((rightMostCol.value + 1) / visibleCols.value) - 1
}

function calculateContainerWidth(container: HTMLElement = grid.value, visibleColumns: number = visibleCols.value) {
    const childCount: number = container.children.length
    const containerWidth: number = container.parentElement.getBoundingClientRect().width
    let width: number;
    if (visibleColumns === 3 ) {
        width = childCount * ((33.3333334*containerWidth) / 100) + (childCount - 3) * 0.3333 * 16;
    } else if (visibleColumns == 2 ) {
        width = childCount * ((38.5*containerWidth) / 100) + childCount * 16 + 0.5 * 16;
    } else {
        width = childCount * ((80*containerWidth) /100) + childCount * 0.5 * 16 + 0.5 * 16;
    }
    container.style.width = width + "px"
}

function goToPage(nr: number) {
    grid.value.children[nr * visibleCols.value].scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" })
}

function prevPage() {
    goToPage(activeDot.value - 1)
}

function nextPage() {
    if (activeDot.value + 1 >= Math.ceil(cols.value / visibleCols.value)) {
        goToPage(activeDot.value)
    } else {
        goToPage(activeDot.value + 1)
    }
}

function onScroll() {
    checkVis()
}
</script>

<style scoped></style>
